import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/evaluate-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | EValuate Alternative for Small UK Builders",
  "description": "Looking for an EValuate (eque2) alternative for small UK builders? The Proper Tool offers self-serve AI quoting with a 31-category UK cost library and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "EValuate Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best EValuate alternative for small UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For small UK builders and sole traders who need self-serve construction quoting without enterprise pricing and demo-gated access, The Proper Tool is the strongest EValuate alternative. EValuate by eque2 is enterprise estimating software for large contractors and housebuilders. The Proper Tool starts at £29/month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "What is EValuate and who is it for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EValuate is enterprise estimating and cost management software developed by eque2 (eque2-estimating.co.uk). It is aimed at large UK contractors, main contractors, and housebuilders with dedicated estimating departments, enterprise procurement budgets, and complex NEC/JCT contract requirements. Case studies on their website feature firms like Lovell and Kingerlee.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I trial EValuate without speaking to sales?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. EValuate is demo-gated: to see the product you must request a demonstration and go through an enterprise sales process. There is no self-serve trial and pricing is not published. The Proper Tool offers a 14-day free trial with no credit card required and you can start quoting immediately.",
      },
    },
    {
      "@type": "Question",
      "name": "Is EValuate suitable for residential extension and alteration work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EValuate is primarily designed for large main contractors and housebuilders working on substantial commercial and residential schemes. It is not suited to the extension, loft conversion, and structural alteration work that small residential builders predominantly do. The Proper Tool is built specifically for this type of work.",
      },
    },
    {
      "@type": "Question",
      "name": "Does EValuate have AI quote generation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EValuate does not offer AI scope generation from project descriptions or drawings. Its estimating module is based on traditional manual takeoff and pricing. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete quote.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  evaluate: string;
  evaluateState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Suitable for small builders and sole traders", evaluate: "No: enterprise focus", evaluateState: "no", tpt: "Yes: built for this", tptState: "yes" },
  { feature: "Self-serve trial (no demo required)", evaluate: "No: demo-gated only", evaluateState: "no", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Transparent published pricing", evaluate: "No: enterprise-quoted", evaluateState: "no", tpt: "Yes: from £29/month", tptState: "yes" },
  { feature: "AI scope generation from description", evaluate: "No: manual entry only", evaluateState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", evaluate: "No", evaluateState: "no", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Suited to extensions and alterations", evaluate: "No: large contractor focus", evaluateState: "no", tpt: "Yes: built for this work", tptState: "yes" },
  { feature: "Scope completeness validation", evaluate: "No", evaluateState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "NEC/JCT contract support", evaluate: "Yes (enterprise feature)", evaluateState: "yes", tpt: "No (residential focus)", tptState: "no" },
  { feature: "Labour / materials / plant split", evaluate: "Yes (detailed)", evaluateState: "yes", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Branded PDF quote output", evaluate: "Yes", evaluateState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Time to first quote", evaluate: "Enterprise onboarding required", evaluateState: "no", tpt: "Under 10 minutes", tptState: "yes" },
  { feature: "Credit card required to trial", evaluate: "No self-serve trial available", evaluateState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Enterprise product designed for large contractors, not small builders",
    desc: "EValuate targets main contractors, housebuilders, and large building firms with dedicated estimating departments. Its case studies feature firms doing substantial commercial and residential schemes. For sole traders and small builders doing extension or alteration work, it is categorically not designed for them.",
  },
  {
    title: "Demo-gated with no self-serve trial",
    desc: "To access EValuate you must request a demonstration and go through an enterprise sales process. There is no self-serve trial and no published pricing. For builders who want to evaluate software on real jobs before committing, this high-friction onboarding is a significant barrier.",
  },
  {
    title: "No AI scope generation or drawing interpretation",
    desc: "EValuate is a traditional estimating platform based on manual takeoff and pricing. There is no AI that reads a project description and generates a scope, and no ability to upload drawings for automatic interpretation.",
  },
  {
    title: "No pricing transparency",
    desc: "EValuate does not publish pricing. Enterprise procurement budgets are assumed. For small builders who need to understand their software costs clearly, this lack of transparency makes proper evaluation difficult.",
  },
];

const STAY_ON_EVALUATE = [
  "You are a large main contractor or housebuilder with a dedicated estimating department and an enterprise procurement budget.",
  "Your projects require NEC or JCT contract compliance and you need the depth of a traditional enterprise estimating system.",
  "You have already completed EValuate implementation and have established estimating workflows in place.",
  "You are bidding on large commercial or residential schemes where enterprise-grade cost management is a requirement.",
];

const SWITCH_REASONS = [
  "You are a small builder or sole trader and enterprise estimating software is simply not designed for your business.",
  "You want to trial software immediately on real jobs without going through a sales demonstration process.",
  "You need transparent pricing so you can make an informed decision about what you are paying.",
  "You want AI-powered scope generation from drawings or a project description.",
  "Your work is residential extensions, loft conversions, and structural alterations, not large commercial schemes.",
  "You want to be producing professional quotes within minutes, not after an enterprise onboarding programme.",
];

const FAQS = [
  {
    q: "What is the best EValuate alternative for small UK builders?",
    a: "For small UK builders and sole traders who need self-serve construction quoting without enterprise pricing and demo-gated access, The Proper Tool is the strongest EValuate alternative. EValuate by eque2 is enterprise estimating software for large contractors. The Proper Tool starts at £29/month with a 14-day free trial and no credit card required.",
  },
  {
    q: "What is EValuate and who is it for?",
    a: "EValuate is enterprise estimating and cost management software by eque2. It is aimed at large UK contractors and housebuilders with dedicated estimating departments and enterprise procurement budgets. Case studies on their website feature firms like Lovell and Kingerlee.",
  },
  {
    q: "Can I trial EValuate without speaking to sales?",
    a: "No. EValuate is demo-gated: to see the product you must request a demonstration and go through an enterprise sales process. The Proper Tool offers a 14-day free trial with no credit card required and you can start quoting immediately.",
  },
  {
    q: "Is EValuate suitable for residential extension and alteration work?",
    a: "EValuate is designed for large main contractors and housebuilders, not small builders doing extension, loft conversion, and structural alteration work. The Proper Tool is built specifically for this type of residential work.",
  },
  {
    q: "Does EValuate have AI quote generation?",
    a: "No. EValuate does not offer AI scope generation from project descriptions or drawings. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete quote.",
  },
];

export default function EvaluateAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="EValuate Alternative for Small UK Builders: Self-Serve Quoting Without the Enterprise Overhead | The Proper Tool"
        description="Looking for an EValuate (eque2) alternative for small UK builders? The Proper Tool offers self-serve AI quoting with a 31-category UK cost library and a 14-day free trial from £29/mo."
        canonical="/evaluate-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>EValuate Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              EValuate Alternative for Small UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              EValuate by eque2 is enterprise estimating software for large UK contractors. If you are a small builder or sole trader who needs self-serve quoting without enterprise pricing and demo-gated access, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why small builders look for an EValuate alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EValuate is a capable enterprise estimating platform. But for small builders and sole traders, the mismatches are fundamental:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs EValuate: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">EValuate</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.evaluate} state={row.evaluateState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with eque2 before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EValuate does not publish its pricing. It is enterprise-quoted through the eque2 sales team and assumes enterprise procurement budgets. For small builders and sole traders, this lack of pricing transparency makes it essentially inaccessible. The Proper Tool starts at £29 per month with transparent, published pricing and a 14-day free trial.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">EValuate (eque2)</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No published pricing: enterprise-quoted</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No self-serve trial: demo required</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Designed for 50+ person firms with dedicated estimating teams</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No AI scope generation or drawing interpretation</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month, published and transparent</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Built for sole traders and small building firms</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />AI-powered, UK cost library built in from day one</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on EValuate</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              EValuate is genuinely the right tool for a specific type of large construction business. If the following describes your organisation, it may be the right choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_EVALUATE.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons small builders choose The Proper Tool over EValuate</h2>
            <p className="text-lg text-muted-foreground mb-8">EValuate is simply not designed for small builders. The Proper Tool is.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Self-serve quoting for small UK builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with eque2 before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/cubit-alternative", label: "Cubit Alternative" },
                { href: "/estimatorxpress-alternative", label: "EstimatorXpress Alternative" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
